class ProductPersonalize extends HTMLElement {
  constructor() {
    super();
    this.currentFont =
      this.querySelector('input[name="font-type"]:checked')?.value || "Roboto";
    this.currentHorizontalFont =
      this.querySelector('input[name="horizontal-font-type"]:checked')?.value ||
      "Roboto";
    this.currentEmojiHorizontalFont =
      this.querySelector('input[name="emoji-horizontal-font-type"]:checked')
        ?.value || "Roboto";
    this.currentIconHorizontalFont =
      this.querySelector('input[name="icon-horizontal-font-type"]:checked')
        ?.value || "Roboto";
    this.horizontalText = this.querySelector("#horizontal-text")?.value || "";
    this.verticalText = this.querySelector("#vertical-text")?.value || "";
    this.emojiHorizontalText =
      this.querySelector("#emoji-horizontal-text")?.value || "";
    this.iconHorizontalText =
      this.querySelector("#icon-horizontal-text")?.value || "";
    this.canvas = document.getElementById("customizer");
    this.color = this.canvas.dataset.textColor || "#fff"; // Get color from data attribute or default to white
    this.currentFontSizeVertical = 25; // Default font size
    this.currentFontSizeHorizontal = 20; // Default font size for horizontal text
    this.currentFontSizeEmoji = 20; // Default font size for emoji template
    this.currentFontSizeIcon = 20; // Default font size for icon template
    this.uploadedImage = null;
    this.emojiNameInput = document.getElementById("emoji-name");
    this.iconNameInput = document.getElementById("icon-name");
    this.personalizeButton = document.querySelector(
      ".personalize__click__btn button"
    );
    this.backButton = document.querySelector(
      ".personalize__success__btn__back"
    );
    this.personalizeContent = this.querySelector(".personalize__content");
    this.personalizeSuccess = this.querySelector(".personalize__success");
    this.personalizeClick = document.querySelector(".personalize__click");

    // Check if vertical/horizontal fields are hidden
    this.isVerticalHidden =
      this.querySelector(".vertical__text").style.display === "none";
    this.isHorizontalHidden =
      this.querySelector(".horizontal__text").style.display === "none";

    // Read the data attribute for small bottle
    this.isSmallBottle = this.dataset.isSmallBottle === "true";

    // Store original maxLength values
    this.originalMaxLengths = new Map();
    this.textInputs = this.querySelectorAll('input[type="text"]');
    this.textInputs.forEach((input) => {
      this.originalMaxLengths.set(input, input.getAttribute("maxlength"));
    });

    // Only create container if it doesn't exist and both content and success exist
    if (
      this.personalizeContent &&
      this.personalizeSuccess &&
      !this.querySelector(".personalize__content-container")
    ) {
      const contentContainer = document.createElement("div");
      contentContainer.className = "personalize__content-container";

      // Move content and success into container
      this.personalizeContent.parentNode.insertBefore(
        contentContainer,
        this.personalizeContent
      );
      contentContainer.appendChild(this.personalizeContent);
      contentContainer.appendChild(this.personalizeSuccess);
    }

    // Initialize button state
    if (this.personalizeClick) {
      this.personalizeClick.classList.add("active");
    }
  }

  connectedCallback() {
    this.setupEventListeners();
    this.initializeCanvas();
    this.checkTextInputs(); // Initial check
    this.setupMaxLengthObserver();

    const activeTemplate = document.querySelector(
      ".template__result [data-result-template].visible"
    );
    if (activeTemplate) {
      this.updateTemplate(activeTemplate);
    }

    // Initialize the content states
    if (this.personalizeContent && this.personalizeSuccess) {
      this.personalizeContent.classList.add("active");
      this.personalizeSuccess.classList.add("inactive");
    }

    // Add event listeners for personalize and back buttons
    if (this.personalizeButton) {
      this.personalizeButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.showSuccessContent();
      });
    }
    if (this.backButton) {
      this.backButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.showPersonalizeContent();
      });
    }

    // Add event listener for close button
    const closeButton = this.querySelector("modal-close");
    if (closeButton) {
      closeButton.addEventListener("click", () => {
        this.handleModalClose();
      });
    }

    // Add event listener for outside click
    const modal = this.closest("modal-dialog");
    if (modal) {
      modal.addEventListener("click", (e) => {
        // Check if the click was outside the modal content
        if (e.target === modal) {
          this.handleModalClose();
        }
      });
    }

    // Add event listener for add to bag form submission
    const addToBagForm = this.querySelector('form[action="/cart/add"]');
    if (addToBagForm) {
      addToBagForm.addEventListener("submit", (e) => {
        // Remove overflow-hidden from body
        document.body.classList.remove("overflow-hidden");
        this.handleModalClose();
      });
    }

    // Set maxlength for emoji and icon template text inputs
    const emojiTextInput = this.querySelector("#emoji-horizontal-text");
    const iconTextInput = this.querySelector("#icon-horizontal-text");
    if (emojiTextInput) {
      emojiTextInput.setAttribute("maxlength", "09");
    }
    if (iconTextInput) {
      iconTextInput.setAttribute("maxlength", "09");
    }
  }

  setupEventListeners() {
    // Font selection listeners for vertical text
    const fontInputs = this.querySelectorAll('input[name="font-type"]');
    fontInputs.forEach((input) => {
      input.addEventListener("change", (e) => {
        this.currentFont = e.target.value;
        this.renderCanvasWithCurrentSettings();
      });
    });

    // Font selection listeners for horizontal text in vertical-horizontal template
    const horizontalFontInputs = this.querySelectorAll(
      'input[name="horizontal-font-type"]'
    );
    horizontalFontInputs.forEach((input) => {
      input.addEventListener("change", (e) => {
        this.currentHorizontalFont = e.target.value;
        this.renderCanvasWithCurrentSettings();
      });
    });

    // Font selection listeners for horizontal text in emoji template
    const emojiHorizontalFontInputs = this.querySelectorAll(
      'input[name="emoji-horizontal-font-type"]'
    );
    emojiHorizontalFontInputs.forEach((input) => {
      input.addEventListener("change", (e) => {
        this.currentEmojiHorizontalFont = e.target.value;
        // Force canvas update for emoji template
        if (document.querySelector(".text__with__emoji.visible")) {
          this.renderCanvasWithCurrentSettings(false);
        }
      });
    });

    // Font selection listeners for horizontal text in icon template
    const iconHorizontalFontInputs = this.querySelectorAll(
      'input[name="icon-horizontal-font-type"]'
    );
    iconHorizontalFontInputs.forEach((input) => {
      input.addEventListener("change", (e) => {
        this.currentIconHorizontalFont = e.target.value;
        // Force canvas update for icon template
        if (document.querySelector(".text__with__icon.visible")) {
          this.renderCanvasWithCurrentSettings(false);
        }
      });
    });

    // Get all text inputs
    const verticalTextInput = this.querySelector("#vertical-text");
    const horizontalTextInput = this.querySelector("#horizontal-text");
    const emojiHorizontalTextInput = this.querySelector(
      "#emoji-horizontal-text"
    );
    const iconHorizontalTextInput = this.querySelector("#icon-horizontal-text");

    // Function to update input states for vertical/horizontal template
    const updateInputStates = () => {
      const hasVerticalText = verticalTextInput?.value.trim().length > 0;
      const hasHorizontalText = horizontalTextInput?.value.trim().length > 0;
      const isHydraPersonalisation = this.hasAttribute(
        "data-hydra-personalisation"
      );

      if (verticalTextInput && horizontalTextInput) {
        if (isHydraPersonalisation) {
          // For hydra personalisation, both fields can be enabled
          verticalTextInput.disabled = false;
          horizontalTextInput.disabled = false;
          verticalTextInput.classList.remove("disabled");
          horizontalTextInput.classList.remove("disabled");
        } else {
          // For other products, maintain the original behavior
          // Disable horizontal input if vertical has text
          horizontalTextInput.disabled = hasVerticalText;
          if (hasVerticalText) {
            horizontalTextInput.value = "";
            this.horizontalText = "";
          }

          // Disable vertical input if horizontal has text
          verticalTextInput.disabled = hasHorizontalText;
          if (hasHorizontalText) {
            verticalTextInput.value = "";
            this.verticalText = "";
          }

          // Update visual feedback
          if (hasVerticalText) {
            horizontalTextInput.classList.add("disabled");
          } else {
            horizontalTextInput.classList.remove("disabled");
          }

          if (hasHorizontalText) {
            verticalTextInput.classList.add("disabled");
          } else {
            verticalTextInput.classList.remove("disabled");
          }
        }
      }
    };

    // Function to handle text input validation and updates
    const handleTextInput = (input, propertyName) => {
      if (!input) return;

      const updateText = (value) => {
        const validatedValue = this.validateInput(value);
        if (value !== validatedValue) {
          input.value = validatedValue;
        }
        this.enforceMaxLength(input);
        this[propertyName] = input.value;

        if (
          propertyName === "verticalText" ||
          propertyName === "horizontalText"
        ) {
          updateInputStates();
        }

        this.renderCanvasWithCurrentSettings();
        this.checkTextInputs();
      };

      // Handle input event
      input.addEventListener("input", (e) => {
        // Prevent the first character from being a space
        if (e.target.value.length === 1 && e.target.value === " ") {
          e.target.value = "";
          return;
        }

        updateText(e.target.value);
      });

      // Handle change event (for browser autocomplete)
      input.addEventListener("change", (e) => {
        updateText(e.target.value);
      });

      // Handle paste event
      input.addEventListener("paste", (e) => {
        // Use setTimeout to ensure the pasted value is in the input
        setTimeout(() => {
          updateText(input.value);
        }, 0);
      });

      // Add keydown event listener to prevent typing when maxLength is reached
      input.addEventListener("keydown", (e) => {
        const maxLength = parseInt(input.getAttribute("maxlength"));
        if (
          input.value.length >= maxLength &&
          !e.ctrlKey &&
          !e.metaKey &&
          !e.altKey &&
          e.key !== "Backspace" &&
          e.key !== "Delete" &&
          e.key !== "ArrowLeft" &&
          e.key !== "ArrowRight" &&
          e.key !== "Tab"
        ) {
          e.preventDefault();
        }
      });
    };

    // Set up event listeners for all text inputs
    handleTextInput(verticalTextInput, "verticalText");
    handleTextInput(horizontalTextInput, "horizontalText");
    handleTextInput(emojiHorizontalTextInput, "emojiHorizontalText");
    handleTextInput(iconHorizontalTextInput, "iconHorizontalText");

    // Initial state update
    updateInputStates();
  }

  validateInput(value) {
    // Remove any special characters except # and spaces, and remove numbers
    let cleanedValue = value.replace(/[^a-zA-Z\s#]/g, "");

    // Ensure only one # character
    const hashCount = (cleanedValue.match(/#/g) || []).length;
    if (hashCount > 1) {
      cleanedValue = cleanedValue.replace(/#/g, "");
      cleanedValue = "#" + cleanedValue;
    }

    // Remove consecutive spaces
    cleanedValue = cleanedValue.replace(/\s+/g, " ");

    return cleanedValue;
  }

  enforceMaxLength(input) {
    const maxLength = parseInt(input.getAttribute("maxlength"));
    if (input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
    }
  }

  initializeCanvas() {
    const imgContainer = this.querySelector(".personalize__image");
    const baseImageSrc = imgContainer.dataset.img;

    this.baseImage = new Image();
    this.baseImage.src = baseImageSrc;
    this.baseImage.onload = () => {
      this.renderCanvasWithCurrentSettings();
    };
  }

  renderCanvasWithCurrentSettings(showVerticalText = true) {
    if (this.baseImage && this.baseImage.complete) {
      const options = {
        font: this.currentFont || "Roboto",
        color: this.color,
        fontSize: this.currentFontSizeVertical,
      };

      // Only show vertical text if it's not hidden
      const verticalTextToRender =
        !this.isVerticalHidden && showVerticalText ? this.verticalText : null;
      // Only show horizontal text if it's not hidden
      const horizontalTextToRender = !this.isHorizontalHidden
        ? this.horizontalText
        : null;

      // Determine which font and font size to use based on the active template
      if (document.querySelector(".text__with__emoji.visible")) {
        options.font = this.currentEmojiHorizontalFont || "Roboto";
        options.fontSize = this.currentFontSizeEmoji;
      } else if (document.querySelector(".text__with__icon.visible")) {
        options.font = this.currentIconHorizontalFont || "Roboto";
        options.fontSize = this.currentFontSizeIcon;
      }

      this.renderCanvas(
        this.baseImage,
        horizontalTextToRender,
        verticalTextToRender,
        options
      );
      this.updateFormInputs();
    }
  }

  renderCanvas(baseImage, horizontalText, verticalText = null, options = {}) {
    const mainCanvas = document.getElementById("customizer");
    const context = mainCanvas.getContext("2d");
    const imageHeightToRender = mainCanvas.height;
    const imageWidthToRender = mainCanvas.width;

    // Helper function to check if text is ALL CAPS
    const isAllCaps = (text) => {
      return text === text.toUpperCase() && text !== text.toLowerCase();
    };

    // Helper function to calculate text width
    const getTextWidth = (text, font) => {
      context.font = font;
      return context.measureText(text).width;
    };

    // Helper function to get adjusted font size based on text width
    // maxWidth should represent the constraint in the direction of the text's measured width (which is vertical on canvas after rotation)
    const getAdjustedFontSize = (
      text,
      baseFontSize,
      font,
      maxWidth,
      isHorizontal = false,
      isVertical = false
    ) => {
      // Set initial font size
      let fontSize = baseFontSize;

      // Check if we're in emoji or icon template
      const isEmojiTemplate = document.querySelector(
        ".text__with__emoji.visible"
      );
      const isIconTemplate = document.querySelector(
        ".text__with__icon.visible"
      );

      // Increase base font size for both templates, but more for icon
      if (isIconTemplate) {
        fontSize = baseFontSize * 1.5; // Increase base size by 50% for icon
      } else if (isEmojiTemplate) {
        fontSize = baseFontSize * 1.2; // Increase base size by 20% for emoji
      }

      // Apply a more significant initial reduction for ALL CAPS text based on orientation
      if (isHorizontal && isAllCaps(text)) {
        fontSize = fontSize * 0.7; // More reduction for horizontal ALL CAPS
      } else if (isVertical && isAllCaps(text)) {
        fontSize = fontSize * 0.7; // More reduction for vertical ALL CAPS (same as horizontal for now)
      } else if (isAllCaps(text)) {
        fontSize = fontSize * 0.8; // Keep existing reduction for generic ALL CAPS (shouldn't be hit with isHorizontal/isVertical)
      }

      // Calculate text width with current font size
      let textWidth = getTextWidth(text, `${fontSize}px ${font}`);

      // If text is too wide, gradually reduce font size until it fits
      while (textWidth > maxWidth && fontSize > 16) {
        // Increased minimum font size threshold from 12 to 8
        fontSize -= 1;
        textWidth = getTextWidth(text, `${fontSize}px ${font}`);
      }

      return fontSize;
    };

    // Calculate bottle dimensions based on product tag
    let bottleWidth, bottleHeight;
    if (this.isSmallBottle) {
      // Dimensions for "Small Water Bottles"
      bottleWidth = imageWidthToRender * 0.6;
      bottleHeight = imageHeightToRender * 0.6;
    } else {
      // Dimensions for other bottles (taller, narrower)
      bottleWidth = imageWidthToRender * 0.4;
      bottleHeight = imageHeightToRender * 0.85;
    }

    const bottleX = (imageWidthToRender - bottleWidth) / 2;
    const bottleY = (imageHeightToRender - bottleHeight) / 2;

    // Helper function to approximate bottle width at a given height (relative to bottle top)
    const getBottleWidthAtHeight = (y, bottleHeight, bottleWidth) => {
      // Simple parabolic approximation for width tapering towards top/bottom
      // This assumes the widest point is in the middle (y = bottleHeight / 2)
      const relativeY = y / bottleHeight; // Normalize height to be between 0 and 1
      const curveFactor = 1 - Math.pow(relativeY - 0.5, 2) * 4; // Factor is 1 at center, 0 at top/bottom
      return bottleWidth * Math.max(0, curveFactor);
    };

    // Clear canvas
    context.clearRect(0, 0, mainCanvas.width, mainCanvas.height);

    // Check if we're in text-with-icon template
    const isTextWithIcon = document.querySelector(".text__with__icon.visible");

    if (isTextWithIcon && this.selectedIcon) {
      // For text-with-icon template, use the selected icon as the base image
      const iconElement = document.getElementById(this.selectedIcon);
      const iconImage =
        iconElement.previousElementSibling.querySelector(".base-img");

      if (iconImage && iconImage.complete) {
        // Draw the image at its original size
        context.drawImage(
          iconImage,
          0,
          0,
          imageWidthToRender,
          imageHeightToRender
        );
      }
    } else {
      // For other templates, use the original base image
      const { naturalHeight: imgHeight, naturalWidth: imgWidth } = baseImage;
      context.drawImage(
        baseImage,
        0,
        0,
        imgWidth,
        imgHeight,
        0,
        0,
        imageWidthToRender,
        imageHeightToRender
      );
    }
    context.save();

    // Use the color from data attribute if available, otherwise use the passed options color
    const textColor = mainCanvas.dataset.textColor || options.color;

    // Render horizontal text if available
    if (horizontalText) {
      // Calculate maximum allowed width for horizontal text (70% of bottle width)
      const maxHorizontalWidth = bottleWidth * 0.6;
      const maxDimensionHorizontal = document
        .querySelector(".axis_values__horizontal")
        .innerHTML.trim();

      // Parse the JSON string into a JavaScript object
      let parsedData = JSON.parse(maxDimensionHorizontal);

      // Access the width from the first object in the array
      let width = parsedData[0].width / 100;
      let height = parsedData[0].height / 100;

      // Determine which font to use based on the active template
      let horizontalFont = this.currentHorizontalFont;
      let fontSize = options.fontSize;

      // Double the font size if Waterfall is selected
      if (horizontalFont === "Waterfall") {
        fontSize = fontSize * 2;
      }

      // Update font name for Verlag
      if (horizontalFont === "Verlag") {
        horizontalFont = "Verlag Bold";
      }

      // Get adjusted font size based on text width
      fontSize = getAdjustedFontSize(
        horizontalText,
        fontSize,
        horizontalFont,
        maxHorizontalWidth,
        true
      );

      // Calculate text position
      let horizontalX, horizontalY;

      if (document.querySelector(".text__with__emoji.visible")) {
        horizontalFont = this.currentEmojiHorizontalFont;
        if (horizontalFont === "Waterfall") {
          fontSize = options.fontSize * 2;
        }
        horizontalY = bottleY + bottleHeight * height;
        horizontalX = imageWidthToRender * width; // Center horizontally
      } else if (document.querySelector(".text__with__icon.visible")) {
        horizontalFont = this.currentIconHorizontalFont;
        if (horizontalFont === "Waterfall") {
          fontSize = options.fontSize * 2;
        }
        horizontalY = bottleY + bottleHeight * 0.95;
        horizontalX = imageWidthToRender * (49 / 100);
      } else {
        // If vertical text is hidden, center the horizontal text vertically
        if (this.isVerticalHidden) {
          horizontalY = bottleY + bottleHeight * height;
        } else {
          horizontalY = bottleY + bottleHeight * height;
        }
        horizontalX = imageWidthToRender * width; // Moved from 50 to 52 to shift text slightly right
      }

      // Calculate dynamic max width for horizontal text at its vertical position
      const dynamicMaxHorizontalWidth =
        getBottleWidthAtHeight(
          horizontalY - bottleY,
          bottleHeight,
          bottleWidth
        ) * 0.85; // Reduced from 0.9

      // Re-adjust font size based on the dynamic max width, indicating it's horizontal text
      fontSize = getAdjustedFontSize(
        horizontalText,
        options.fontSize,
        horizontalFont,
        dynamicMaxHorizontalWidth,
        true
      );

      context.save();
      context.rotate((0 * Math.PI) / 180);
      context.fillStyle = textColor;

      // Add font weight for Verdana
      if (horizontalFont === "Verdana") {
        context.font = `900 ${fontSize}px ${horizontalFont}`;
      } else {
        context.font = `${fontSize}px ${horizontalFont}`;
      }
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(horizontalText, horizontalX, horizontalY);
      context.restore();
    }

    // Render vertical text if available
    if (verticalText) {
      const maxDimensionVertical = document
        .querySelector(".axis_values__vertical")
        .innerHTML.trim();

      // Parse the JSON string into a JavaScript object
      let parsedData = JSON.parse(maxDimensionVertical);

      // Access the width from the first object in the array
      let width = parsedData[0].width / 100;
      let height = parsedData[0].height / 100;

      // Calculate maximum allowed vertical dimension for vertical text (corresponds to text width after rotation)
      const maxVerticalDimension = bottleHeight * 0.7; // Reduced from 0.75 to tighten constraint

      const verticalX = imageWidthToRender * width;
      const verticalY = bottleY + bottleHeight * height; // 60% from top of bottle

      context.save();
      context.translate(verticalX, verticalY);
      context.rotate((-90 * Math.PI) / 180);
      context.fillStyle = textColor;

      // Double the font size if Waterfall is selected for vertical text
      let verticalFontSize = options.fontSize;
      if (this.currentFont === "Waterfall") {
        verticalFontSize = options.fontSize * 2;
      }

      // Update font name for Verlag
      let verticalFont = this.currentFont;
      if (verticalFont === "Verlag") {
        verticalFont = "Verlag Bold";
      }

      // Get adjusted font size based on text width and the maximum allowed vertical dimension
      verticalFontSize = getAdjustedFontSize(
        verticalText,
        verticalFontSize,
        verticalFont,
        maxVerticalDimension,
        false,
        true
      );

      // Add font weight for Verdana
      if (verticalFont === "Verdana") {
        context.font = `900 ${verticalFontSize}px ${verticalFont}`;
      } else {
        context.font = `${verticalFontSize}px ${verticalFont}`;
      }
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(verticalText, 0, 0);
      context.restore();
    }

    if (this.selectedEmoji) {
      const emojiElement = document.getElementById(this.selectedEmoji);
      const emojiImage =
        emojiElement.previousElementSibling.querySelector("img");
      if (emojiImage) {
        if (emojiImage && emojiImage.complete) {
          const emojiSize = 70; // Adjust size as needed
          const emojiX = (imageWidthToRender - emojiSize) / 2; // Center horizontally
          const emojiY = (imageHeightToRender - emojiSize) / 2 + 20; // Center vertically and add 20px offset

          context.drawImage(emojiImage, emojiX, emojiY, emojiSize, emojiSize);
          context.save();
        }
      }
    }

    // Remove the icon rendering here since we're using it as the base image for text-with-icon template
    if (!isTextWithIcon && this.selectedIcon) {
      const iconElement = document.getElementById(this.selectedIcon);
      const iconImage = iconElement.previousElementSibling.querySelector("img");
      if (iconImage) {
        if (iconImage && iconImage.complete) {
          const iconSize = 70; // Adjust size as needed
          const iconX = (imageWidthToRender - iconSize) / 2; // Center horizontally
          const iconY = (imageHeightToRender - iconSize) / 2 + 20; // Center vertically and add 20px offset

          context.drawImage(iconImage, iconX, iconY, iconSize, iconSize);
          context.save();
        }
      }
    }

    // Render uploaded icon if available
    if (this.uploadedImage) {
      const iconSize = 70; // Adjust icon size as needed
      const iconX = (imageWidthToRender - iconSize) / 2; // Center horizontally
      const iconY = (imageHeightToRender - iconSize) / 2; // Center vertically

      context.drawImage(this.uploadedImage, iconX, iconY, iconSize, iconSize);
    }

    context.restore();
  }

  updateTemplate(templateResult) {
    // Fetch horizontal and vertical text value
    let horizontalTextInput = null;
    let verticalTextInput = null;

    // Get the vertical font selector
    const verticalFontSelector = document.querySelector(
      '.font__selector:has(input[name="font-type"])'
    );
    const horizontalFontSelector = document.querySelector(
      '.font__selector:has(input[name="horizontal-font-type"])'
    );
    const emojiFontSelector = document.querySelector(
      '.font__selector:has(input[name="emoji-horizontal-font-type"])'
    );
    const iconFontSelector = document.querySelector(
      '.font__selector:has(input[name="icon-horizontal-font-type"])'
    );

    if (templateResult.className.includes("vertical__horizontal__text")) {
      horizontalTextInput = templateResult.querySelector("#horizontal-text");
      verticalTextInput = templateResult.querySelector("#vertical-text");

      // Show vertical font selector
      if (verticalFontSelector) {
        verticalFontSelector.style.opacity = "1";
        verticalFontSelector.style.position = "relative";
        verticalFontSelector.style.transform = "scale(1)";
        verticalFontSelector.style.zIndex = "1";
        verticalFontSelector.style.pointerEvents = "auto";
      }
      if (horizontalFontSelector) {
        horizontalFontSelector.style.opacity = "1";
        horizontalFontSelector.style.position = "relative";
        horizontalFontSelector.style.transform = "scale(1)";
        horizontalFontSelector.style.zIndex = "1";
        horizontalFontSelector.style.pointerEvents = "auto";
      }

      if (verticalTextInput) {
        this.verticalText = verticalTextInput.value;
        verticalTextInput.addEventListener("input", (e) => {
          this.verticalText = e.target.value.trim();
          this.renderCanvasWithCurrentSettings(true);
        });
      }
      if (horizontalTextInput) {
        this.horizontalText = horizontalTextInput.value;
        horizontalTextInput.addEventListener("input", (e) => {
          this.horizontalText = e.target.value.trim();
          this.renderCanvasWithCurrentSettings(true);
        });
      }
      this.selectedEmoji = null;
      this.selectedIcon = null;
      this.renderCanvasWithCurrentSettings(true);
      this.iconNameInput.value = " ";
      this.emojiNameInput.value = " ";
    }

    if (
      templateResult.className.includes("text__with__emoji") ||
      templateResult.className.includes("emoji__list__wrapper")
    ) {
      horizontalTextInput = templateResult.querySelector(
        "#emoji-horizontal-text"
      );
      this.verticalText = "";

      // Hide vertical font selector and show emoji font selector
      if (verticalFontSelector) {
        verticalFontSelector.style.opacity = "0";
        verticalFontSelector.style.position = "absolute";
        verticalFontSelector.style.transform = "scale(0.95)";
        verticalFontSelector.style.zIndex = "-1";
        verticalFontSelector.style.pointerEvents = "none";
      }
      if (horizontalFontSelector) {
        horizontalFontSelector.style.opacity = "0";
        horizontalFontSelector.style.position = "absolute";
        horizontalFontSelector.style.transform = "scale(0.95)";
        horizontalFontSelector.style.zIndex = "-1";
        horizontalFontSelector.style.pointerEvents = "none";
      }
      if (emojiFontSelector) {
        emojiFontSelector.style.opacity = "1";
        emojiFontSelector.style.position = "relative";
        emojiFontSelector.style.transform = "scale(1)";
        emojiFontSelector.style.zIndex = "1";
        emojiFontSelector.style.pointerEvents = "auto";
      }

      if (horizontalTextInput) {
        this.horizontalText = horizontalTextInput.value;
        horizontalTextInput.addEventListener("input", (e) => {
          this.horizontalText = e.target.value.trim();
          this.renderCanvasWithCurrentSettings(false);
        });
      }

      // Select first emoji if none is selected
      const selectedEmoji =
        templateResult.querySelector(".emoji__selector:checked") ||
        templateResult.querySelector(".emoji__selector");
      if (selectedEmoji) {
        selectedEmoji.checked = true;
        let emojiFileName = selectedEmoji.dataset.fileName;
        this.emojiNameInput.value = emojiFileName;
        this.iconNameInput.value = " ";
        this.selectedEmoji = selectedEmoji.id;
        this.selectedIcon = null;
      }

      templateResult.querySelectorAll(".emoji__selector").forEach((input) => {
        input.addEventListener("change", (e) => {
          this.emojiNameInput.value = "";
          this.selectedEmoji = e.target.id;
          this.emojiNameInput.value = e.target.dataset.fileName;
          this.iconNameInput.value = " ";
          this.renderCanvasWithCurrentSettings(false);
        });
      });

      this.renderCanvasWithCurrentSettings(false);
      document.querySelector("#selected-font-1").value = " ";
    }

    if (
      templateResult.className.includes("text__with__icon") ||
      templateResult.className.includes("emoji__list__wrapper")
    ) {
      horizontalTextInput = templateResult.querySelector(
        "#icon-horizontal-text"
      );
      this.verticalText = "";

      // Hide vertical font selector and show icon font selector
      if (verticalFontSelector) {
        verticalFontSelector.style.opacity = "0";
        verticalFontSelector.style.position = "absolute";
        verticalFontSelector.style.transform = "scale(0.95)";
        verticalFontSelector.style.zIndex = "-1";
        verticalFontSelector.style.pointerEvents = "none";
      }
      if (horizontalFontSelector) {
        horizontalFontSelector.style.opacity = "0";
        horizontalFontSelector.style.position = "absolute";
        horizontalFontSelector.style.transform = "scale(0.95)";
        horizontalFontSelector.style.zIndex = "-1";
        horizontalFontSelector.style.pointerEvents = "none";
      }
      if (iconFontSelector) {
        iconFontSelector.style.opacity = "1";
        iconFontSelector.style.position = "relative";
        iconFontSelector.style.transform = "scale(1)";
        iconFontSelector.style.zIndex = "1";
        iconFontSelector.style.pointerEvents = "auto";
      }

      if (horizontalTextInput) {
        this.horizontalText = horizontalTextInput.value;
        horizontalTextInput.addEventListener("input", (e) => {
          this.horizontalText = e.target.value.trim();
          this.renderCanvasWithCurrentSettings(false);
        });
      }

      // Select first icon if none is selected
      const selectedIcon =
        templateResult.querySelector(".icon__selector:checked") ||
        templateResult.querySelector(".icon__selector");
      if (selectedIcon) {
        selectedIcon.checked = true;
        let iconFileName = selectedIcon.dataset.fileName;
        this.iconNameInput.value = iconFileName;
        this.emojiNameInput.value = " ";
        this.selectedIcon = selectedIcon.id;
        this.selectedEmoji = null;
      }

      templateResult.querySelectorAll(".icon__selector").forEach((input) => {
        input.addEventListener("change", (e) => {
          this.iconNameInput.value = "";
          this.selectedIcon = e.target.id;
          this.iconNameInput.value = e.target.dataset.fileName;
          this.emojiNameInput.value = " ";
          this.renderCanvasWithCurrentSettings(false);
        });
      });

      this.renderCanvasWithCurrentSettings(false);
      document.querySelector("#selected-font-1").value = " ";
    }
  }

  updateFormInputs() {
    // Update hidden form fields with current canvas data
    const canvaImageInput = document.querySelector("#canva-image");
    const verticalMessageInput = document.querySelector("#vertical-message");
    const horizontalMessageInput = document.querySelector(
      "#horizontal-message"
    );
    const selectedFont1 = document.querySelector("#selected-font-1");
    const selectedFont2 = document.querySelector("#selected-font-2");

    if (canvaImageInput) {
      canvaImageInput.remove();
      const mainCanvas = document.getElementById("customizer");
      const canvasDataURL = mainCanvas.toDataURL("image/png");
      const canvaFile = this.dataURLtoFile(canvasDataURL, "CustomImage"); // Convert canvas to Base64 image URL
      const canvaInput = document.createElement("input");
      canvaInput.type = "file";
      canvaInput.name = "properties[CustomImage]";
      canvaInput.classList.add("tw-hidden");
      canvaInput.id = "canva-image";
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(canvaFile);
      canvaInput.files = dataTransfer.files;
      document
        .querySelector(".personalize__success__btn form")
        .append(canvaInput);
    }

    // Update horizontal and vertical messages
    if (horizontalMessageInput) {
      horizontalMessageInput.value = this.horizontalText || " ";
    }

    if (verticalMessageInput) {
      verticalMessageInput.value = this.verticalText || " ";
    }

    // Update font values based on the active template
    if (selectedFont1) {
      selectedFont1.value = this.verticalText ? this.currentFont : " ";
    }

    if (selectedFont2) {
      if (document.querySelector(".text__with__emoji.visible")) {
        selectedFont2.value = this.horizontalText
          ? this.currentEmojiHorizontalFont
          : " ";
      } else if (document.querySelector(".text__with__icon.visible")) {
        selectedFont2.value = this.horizontalText
          ? this.currentIconHorizontalFont
          : " ";
      } else {
        selectedFont2.value = this.horizontalText
          ? this.currentHorizontalFont
          : " ";
      }
    }
  }

  dataURLtoFile(dataurl, filename) {
    const arr = dataurl.split(","),
      mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  checkTextInputs() {
    if (this.personalizeButton) {
      // If both vertical and horizontal are hidden, enable the button
      if (this.isVerticalHidden && this.isHorizontalHidden) {
        this.personalizeButton.disabled = false;
        return;
      }

      // Check if we're in emoji or icon template
      const isEmojiTemplate = document.querySelector(
        ".text__with__emoji.visible"
      );
      const isIconTemplate = document.querySelector(
        ".text__with__icon.visible"
      );

      if (isEmojiTemplate || isIconTemplate) {
        // For emoji/icon templates, check if there's text and an emoji/icon selected
        const hasText =
          this.horizontalText && this.horizontalText.trim().length > 0;
        const hasEmoji = isEmojiTemplate && this.selectedEmoji;
        const hasIcon = isIconTemplate && this.selectedIcon;

        this.personalizeButton.disabled = !(hasText && (hasEmoji || hasIcon));
      } else {
        // For other templates, check if at least one visible field has text
        const hasVerticalText = !this.isVerticalHidden && this.verticalText;
        const hasHorizontalText =
          !this.isHorizontalHidden && this.horizontalText;
        const isDisabled = !hasVerticalText && !hasHorizontalText;
        this.personalizeButton.disabled = isDisabled;
      }
    }
  }

  showSuccessContent() {
    if (this.personalizeContent && this.personalizeSuccess) {
      // First remove active class from content
      this.personalizeContent.classList.remove("active");
      this.personalizeContent.classList.add("inactive");

      // Hide personalize button wrapper
      const personalizeBtnWrapper = document.querySelector(
        ".personalize__btn__wrapper"
      );
      if (personalizeBtnWrapper) {
        personalizeBtnWrapper.classList.remove("active");
        personalizeBtnWrapper.classList.add("inactive");
      }

      // Scroll content back to top
      const personalizeWrapper = document.querySelector(
        ".personalize__wrapper"
      );
      if (personalizeWrapper) {
        personalizeWrapper.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }

      // After transition, show success content
      setTimeout(() => {
        this.personalizeSuccess.classList.remove("inactive");
        this.personalizeSuccess.classList.add("active");
      }, 300);
    }
  }

  showPersonalizeContent() {
    if (this.personalizeContent && this.personalizeSuccess) {
      // First remove active class from success
      this.personalizeSuccess.classList.remove("active");
      this.personalizeSuccess.classList.add("inactive");

      // Show personalize button wrapper
      const personalizeBtnWrapper = document.querySelector(
        ".personalize__btn__wrapper"
      );
      if (personalizeBtnWrapper) {
        personalizeBtnWrapper.classList.remove("inactive");
        personalizeBtnWrapper.classList.add("active");
      }

      // After transition, show content
      setTimeout(() => {
        this.personalizeContent.classList.remove("inactive");
        this.personalizeContent.classList.add("active");
      }, 300);
    }
  }

  setupMaxLengthObserver() {
    // Create a MutationObserver to watch for changes to maxLength attribute
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "maxlength"
        ) {
          const input = mutation.target;
          const originalMaxLength = this.originalMaxLengths.get(input);

          // If maxLength was changed, restore it to original value
          if (input.getAttribute("maxlength") !== originalMaxLength) {
            input.setAttribute("maxlength", originalMaxLength);

            // Also trim the value if it exceeds the original maxLength
            if (input.value.length > originalMaxLength) {
              input.value = input.value.slice(0, originalMaxLength);
            }
          }
        }
      });
    });

    // Start observing each text input
    this.textInputs.forEach((input) => {
      observer.observe(input, {
        attributes: true,
        attributeFilter: ["maxlength"],
      });
    });
  }

  resetPersonalization() {
    // Reset all text inputs
    this.textInputs.forEach((input) => {
      input.value = "";
    });

    // Reset character counters with correct limits
    const counters = {
      "vertical-text-counter": "00/14",
      "horizontal-text-counter": "00/09",
      "emoj-text-counter": "00/09",
      "image-text-counter": "00/13",
      "icon-text-counter": "00/09",
    };

    Object.entries(counters).forEach(([id, value]) => {
      const counter = document.getElementById(id);
      if (counter) {
        counter.textContent = value;
      }
    });

    // Reset font selections to default (Roboto)
    const fontTypes = [
      "font-type",
      "horizontal-font-type",
      "emoji-horizontal-font-type",
      "icon-horizontal-font-type",
    ];
    fontTypes.forEach((fontType) => {
      const fontInputs = this.querySelectorAll(`input[name="${fontType}"]`);
      if (fontInputs.length > 0) {
        // Uncheck all font inputs
        fontInputs.forEach((input) => {
          input.checked = false;
        });
        // Check the first font input (Roboto)
        const firstFontInput = fontInputs[0];
        if (firstFontInput) {
          firstFontInput.checked = true;
          // Update the current font based on the font type
          switch (fontType) {
            case "font-type":
              this.currentFont = firstFontInput.value;
              break;
            case "horizontal-font-type":
              this.currentHorizontalFont = firstFontInput.value;
              break;
            case "emoji-horizontal-font-type":
              this.currentEmojiHorizontalFont = firstFontInput.value;
              break;
            case "icon-horizontal-font-type":
              this.currentIconHorizontalFont = firstFontInput.value;
              break;
          }
        }
      }
    });

    // Reset font sizes to default values
    this.currentFontSizeVertical = 25;
    this.currentFontSizeHorizontal = 20;
    this.currentFontSizeEmoji = 20;
    this.currentFontSizeIcon = 20;

    // Reset emoji and icon selections
    const emojiSelectors = this.querySelectorAll(".emoji__selector");
    const iconSelectors = this.querySelectorAll(".icon__selector");
    emojiSelectors.forEach((selector) => (selector.checked = false));
    iconSelectors.forEach((selector) => (selector.checked = false));

    // Reset template selection to first tab
    const firstTemplate = this.querySelector(".template__content");
    if (firstTemplate) {
      this.querySelectorAll(".template__content").forEach((content) => {
        content.classList.remove("active");
      });
      firstTemplate.classList.add("active");

      // Show corresponding template result
      const selectedTemplate = firstTemplate.dataset.template;
      this.querySelectorAll("[data-template-result]").forEach((result) => {
        result.classList.remove("visible");
      });
      const selectedResult = this.querySelector(
        `[data-template-result="${selectedTemplate}"]`
      );
      if (selectedResult) {
        selectedResult.classList.add("visible");
      }

      // Show both font selectors for vertical-horizontal text template
      if (selectedTemplate === "vertical-horizontal-text") {
        const verticalFontSelector = this.querySelector(
          '.font__selector:has(input[name="font-type"])'
        );
        const horizontalFontSelector = this.querySelector(
          '.font__selector:has(input[name="horizontal-font-type"])'
        );

        if (verticalFontSelector) {
          verticalFontSelector.style.opacity = "1";
          verticalFontSelector.style.position = "relative";
          verticalFontSelector.style.transform = "scale(1)";
          verticalFontSelector.style.zIndex = "1";
          verticalFontSelector.style.pointerEvents = "auto";
        }

        if (horizontalFontSelector) {
          horizontalFontSelector.style.opacity = "1";
          horizontalFontSelector.style.position = "relative";
          horizontalFontSelector.style.transform = "scale(1)";
          horizontalFontSelector.style.zIndex = "1";
          horizontalFontSelector.style.pointerEvents = "auto";
        }
      }
    }

    // Reset canvas and all related properties
    this.verticalText = "";
    this.horizontalText = "";
    this.emojiHorizontalText = "";
    this.iconHorizontalText = "";
    this.selectedEmoji = null;
    this.selectedIcon = null;
    this.uploadedImage = null;

    // Clear the canvas and redraw with base image only
    const mainCanvas = document.getElementById("customizer");
    if (mainCanvas) {
      const context = mainCanvas.getContext("2d");
      context.clearRect(0, 0, mainCanvas.width, mainCanvas.height);
      if (this.baseImage && this.baseImage.complete) {
        context.drawImage(
          this.baseImage,
          0,
          0,
          mainCanvas.width,
          mainCanvas.height
        );
      }
    }

    // Reset success state
    if (this.personalizeContent && this.personalizeSuccess) {
      this.personalizeSuccess.classList.remove("active");
      this.personalizeSuccess.classList.add("inactive");
      this.personalizeContent.classList.remove("inactive");
      this.personalizeContent.classList.add("active");
    }

    // Show personalize button wrapper
    const personalizeBtnWrapper = document.querySelector(
      ".personalize__btn__wrapper"
    );
    if (personalizeBtnWrapper) {
      personalizeBtnWrapper.classList.remove("inactive");
      personalizeBtnWrapper.classList.add("active");
    }

    // Reset button state
    if (this.personalizeButton) {
      this.personalizeButton.disabled = true;
    }

    // Reset emoji and icon name inputs
    if (this.emojiNameInput) {
      this.emojiNameInput.value = " ";
    }
    if (this.iconNameInput) {
      this.iconNameInput.value = " ";
    }
  }

  handleModalClose() {
    // Close the modal first
    const modal = this.closest("modal-dialog");
    if (modal) {
      modal.close();
    }

    // Reset all font selectors to hidden state
    const fontSelectors = this.querySelectorAll(".font__selector");
    fontSelectors.forEach((selector) => {
      selector.style.opacity = "0";
      selector.style.position = "absolute";
      selector.style.transform = "scale(0.95)";
      selector.style.zIndex = "-1";
      selector.style.pointerEvents = "none";
    });

    // Wait for the modal to close
    setTimeout(() => {
      // Reset personalization after modal is closed
      this.resetPersonalization();
    }, 3500);
  }
}

customElements.define("product-personalize", ProductPersonalize);

class TemplateSelect extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.handleTemplateChange.bind(this));
    this.tab = this.querySelectorAll(".tab__heading");
    this.tab.forEach((tab) =>
      tab.addEventListener("click", (e) => this.handleTabChange(e, tab))
    );
  }

  connectedCallback() {
    this.setupInitialActiveState();
    this.updateCharacterCounter("vertical-text", "vertical-text-counter");
    this.updateCharacterCounter("horizontal-text", "horizontal-text-counter");
    this.updateCharacterCounter("emoji-horizontal-text", "emoj-text-counter");
    this.updateCharacterCounter("image-horizontal-text", "image-text-counter");
    this.updateCharacterCounter("icon-horizontal-text", "icon-text-counter");
  }

  setupInitialActiveState() {
    const activeContent = this.querySelector(".template__content.active");
    if (activeContent) {
      const template = activeContent.dataset.template;
      this.showTemplateResult(template);
    }
  }

  handleTemplateChange(event) {
    const clickedElement = event.target.closest(".template__content");
    if (clickedElement && !clickedElement.classList.contains("active")) {
      // Remove active class from all content
      this.querySelectorAll(".template__content").forEach((content) => {
        content.classList.remove("active");
      });

      // Add active class to the clicked content
      clickedElement.classList.add("active");

      // Show corresponding template result
      const selectedTemplate = clickedElement.dataset.template;
      this.showTemplateResult(selectedTemplate);

      // Notify ProductPersonalize about the new template
      const productPersonalize = document.querySelector("product-personalize");
      const templateResult = this.querySelector(
        `[data-template-result="${selectedTemplate}"]`
      );
      if (productPersonalize && templateResult) {
        productPersonalize.updateTemplate(templateResult);

        // Enable the button when switching to a different tab
        productPersonalize.personalizeButton.disabled = false;

        // Check inputs if returning to "Vertical and Horizontal text"
        if (selectedTemplate === "vertical-horizontal-text") {
          productPersonalize.checkTextInputs();
        }
      }
    }
  }

  showTemplateResult(template) {
    this.querySelectorAll("[data-template-result]").forEach((result) => {
      result.classList.remove("visible");
    });

    const selectedResult = this.querySelector(
      `[data-template-result="${template}"]`
    );
    if (selectedResult) {
      selectedResult.classList.add("visible");
    }
  }

  handleTabChange(e, tab) {
    let closetTab = null;
    const tabElement = e.target.closest(".emoji__tab, .icon__tab");
    const tabParent = tabElement.closest(".emoji__container, .icon__container");
    if (
      tabParent.classList.contains("emoji__container") &&
      tabElement.classList.contains("emoji__tab")
    ) {
      closetTab = tabElement; // Set closetTab if it's emoji__tab
    } else if (
      tabParent.classList.contains("icon__container") &&
      tabElement.classList.contains("icon__tab")
    ) {
      closetTab = tabElement; // Set closetTab if it's icon__tab
    }
    closetTab
      .querySelectorAll(`.tab__heading`)
      .forEach((el) => el.classList.remove("active"));
    tab.classList.add("active");

    const selectedTab = tab.dataset.tab;
    // Hide all emoji lists
    tabParent.querySelectorAll(".emoji__list__wrapper").forEach((list) => {
      if (list.dataset.emoji === selectedTab) {
        list.classList.add("active");
        // Select first emoji/icon in the active list
        const firstSelector = list.querySelector(
          ".emoji__selector, .icon__selector"
        );
        if (firstSelector) {
          firstSelector.checked = true;
          // Trigger change event to update the canvas
          firstSelector.dispatchEvent(new Event("change"));
        }
      } else {
        list.classList.remove("active");
      }
    });

    // Notify ProductPersonalize to update the canvas for the selected tab
    const productPersonalize = document.querySelector("product-personalize");
    if (productPersonalize) {
      const activeEmojiList = this.querySelector(
        `.emoji__list__wrapper[data-emoji="${selectedTab}"]`
      );
      if (activeEmojiList) {
        productPersonalize.updateTemplate(activeEmojiList);
      }
    }
  }

  updateCharacterCounter(inputId, counterId) {
    const inputElement = document.getElementById(inputId);
    const counterElement = document.getElementById(counterId);

    // Function to update the counter text
    const updateCounterText = () => {
      if (inputElement && counterElement) {
        const currentLength = inputElement.value.length; // Get current length of the input
        const maxLength = parseInt(inputElement.getAttribute("maxlength")); // Get max length

        // Format the counter text with leading zeros
        const formattedCurrentLength = currentLength
          .toString()
          .padStart(2, "0");
        const formattedMaxLength = maxLength.toString().padStart(2, "0");

        counterElement.textContent = `${formattedCurrentLength}/${formattedMaxLength}`;
      }
    };

    // Call the update function initially to set the counter based on current value
    updateCounterText();

    // Listen for input changes and update the counter in real time
    if (inputElement) inputElement.addEventListener("input", updateCounterText);
  }
}

customElements.define("template-select", TemplateSelect);
